import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { cloneChildren } from '../_utils';
import styles from './accordion.module.scss';
// eslint-disable-next-line import/no-cycle
import { AccordionItem, } from './accordion-item/accordion-item';
import { AccordionVariant } from './types';
export const Accordion = ({ children, useContainedHeight = true, firstItemExpanded = false, expandMultipleItems = false, className = '', expandedItemsIndexes, variant = AccordionVariant.BASIC, noPadding = false, onAccordionChange = _.noop, useUncontrolledState = false, ...restProps }) => {
    /**
     * State Management Strategy:
     *
     * When useUncontrolledState = false (default - original behavior):
     * - State initialized as empty Set
     * - Component maintains its own internal state for user interactions
     * - useEffect syncs state with props whenever props change
     * - This is a "semi-controlled" pattern - state can be updated by both:
     *   1. User interactions (via onItemClicked)
     *   2. Prop changes (via useEffect)
     * - Problem: Prop changes (even reference-only) reset user's state
     *
     * When useUncontrolledState = true:
     * - State initialized from props (expandedItemsIndexes) once on mount
     * - useEffect skips syncing - no state resets on prop changes
     * - Component is "uncontrolled" - only user interactions update state
     * - This prevents accordion from reopening when parent re-renders
     * - User interactions (expand/collapse) are preserved across re-renders
     */
    const [expandedItems, setExpandedItems] = useState(() => {
        if (!useUncontrolledState) {
            return new Set();
        }
        if (firstItemExpanded) {
            return new Set([0]);
        }
        if (expandedItemsIndexes && expandedItemsIndexes.length > 0) {
            if (expandMultipleItems) {
                return new Set(expandedItemsIndexes);
            }
            if (expandedItemsIndexes.length > 1) {
                // eslint-disable-next-line no-console
                console.warn('Incompatible/conflicting meaning conveyed by props');
                return new Set([expandedItemsIndexes[0]]);
            }
        }
        return new Set();
    });
    // Only sync with props if NOT using uncontrolled state
    // When useUncontrolledState is true, props are only used for initial state
    useEffect(() => {
        if (useUncontrolledState) {
            // Skip syncing - maintain internal state
            return;
        }
        // Original controlled behavior - sync with props
        if (firstItemExpanded) {
            setExpandedItems(new Set([0]));
        }
        else if (expandedItemsIndexes && expandedItemsIndexes.length > 0) {
            if (expandMultipleItems) {
                setExpandedItems(new Set(expandedItemsIndexes));
            }
            else if (expandedItemsIndexes.length > 1) {
                // eslint-disable-next-line no-console
                console.warn('Incompatible/conflicting meaning conveyed by props');
                // When expandMultipleItems is set to true and user sends more than one index in expandedItemsIndexes array, setting only the first index item to be expanded
                setExpandedItems(new Set([expandedItemsIndexes[0]]));
            }
        }
        else {
            setExpandedItems(new Set());
        }
    }, [expandMultipleItems, expandedItemsIndexes, firstItemExpanded, useUncontrolledState]);
    const containerClass = cx(className, styles.accordionWrapper, {
        [styles.noPadding]: noPadding,
        [styles.accordionUseContainerHeight]: useContainedHeight,
        [styles.basicVariant]: variant === AccordionVariant.BASIC ||
            variant === AccordionVariant.SIDE_NAV,
        [styles.multiLevelVariant]: variant === AccordionVariant.MULTI_LEVEL,
        [styles.longListVariant]: variant === AccordionVariant.LONG_LIST,
        [styles.sideNavVariant]: variant === AccordionVariant.SIDE_NAV,
        [styles.minimalVariant]: variant === AccordionVariant.MINIMAL,
    });
    const onItemClicked = (index) => {
        /* Can expand multiple items */
        if (expandMultipleItems) {
            setExpandedItems((oldState) => {
                // Create a copy of the old state
                const newState = new Set(oldState);
                if (oldState.has(index)) {
                    // already expanded; collapse it
                    newState.delete(index);
                }
                else {
                    // not expanded yet; add to the set of expanded items
                    newState.add(index);
                }
                onAccordionChange(Array.from(newState));
                return newState;
            });
        }
        else {
            /* Can expand only one item at a time */
            setExpandedItems((oldState) => {
                if (oldState.has(index)) {
                    // already expanded; collapse it
                    return new Set([]);
                }
                // expand current index; this will automatically collapse the old index
                const newState = new Set([index]);
                onAccordionChange(Array.from(newState));
                return newState;
            });
        }
    };
    return (React.createElement("div", { className: containerClass, ...restProps }, React.Children.toArray(children).map((accordionItem, index) => {
        const isExpanded = expandedItems.has(index);
        const accordionItemProps = {
            isExpanded,
            index,
            onItemClicked,
            variant,
        };
        return cloneChildren(accordionItem, accordionItemProps);
    })));
};
Accordion.Item = AccordionItem;
//# sourceMappingURL=accordion.js.map